import React, { useState } from 'react';
import { useDevelopmentDemo } from '../../contexts/DevelopmentDemoContext';
import type { Referral, ReferralStatus } from '../../types/developmentDemo';
import { formatTimestamp } from '../../utils/demoStorage';
import { errorClass, fieldClass, glassPanel, labelClass, primaryButton } from '../../utils/demoUi';
import { participantName, referralStatusLabels } from '../../utils/developmentDemo';
const statuses = Object.keys(referralStatusLabels) as ReferralStatus[];
export function ReferralCard({
  referral,
  canUpdate
}: {
  referral: Referral;
  canUpdate: boolean;
}) {
  const {
    state,
    config,
    dispatch
  } = useDevelopmentDemo();
  const partner = config.partners?.find(item => item.id === referral.partnerId);
  const draft = state.ui.referralUpdateDrafts[referral.id] ?? {
    status: '',
    reason: ''
  };
  const [error, setError] = useState('');
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!draft.status || draft.status === referral.status) return setError('Choose a new status.');
    if (!draft.reason.trim()) return setError('Give a reason for this status update.');
    setError('');
    dispatch({
      type: 'updateReferral',
      referralId: referral.id
    });
  };
  return <article className={`${glassPanel} p-6`} data-id="element-859" data-mp="35axym3izjid8">
      <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-860" data-mp="357tpqsukryg8">
        <div data-id="element-861" data-mp="3a9iyu2y8m21o">
          <p className="font-mono text-xs font-semibold text-ink-muted" data-id="element-862" data-mp="3a99byo8hob07">{referral.id}</p>
          <h3 className="mt-1 font-heading text-lg font-bold text-ink-dark" data-id="element-863" data-mp="1tpmm0axvcqf">
            {participantName(config, referral.participantId)} → {partner?.name ?? referral.partnerId}
          </h3>
        </div>
        <span className="rounded-full border border-line bg-white px-2.5 py-0.5 text-xs font-semibold text-navy" data-id="element-864" data-mp="3oxm93ojbbzwa">
          {referralStatusLabels[referral.status]}
        </span>
      </div>
      <dl className="mt-4 space-y-3 text-sm" data-id="element-865" data-mp="2dh5q5l8jv14o">
        <div data-id="element-866" data-mp="2hk2dfp91bley">
          <dt className="font-semibold text-ink-dark" data-id="element-867" data-mp="2hkkfj2idotus">Purpose</dt>
          <dd className="text-ink-dark" data-id="element-868" data-mp="2hhzegfpwmhy3">{referral.purpose}</dd>
        </div>
        <div data-id="element-869" data-mp="o0tk0wmicrm8">
          <dt className="font-semibold text-ink-dark" data-id="element-870" data-mp="o3ftx2wvfj7m">Exactly what was shared</dt>
          <dd className="mt-1 whitespace-pre-wrap rounded-lg border-l-4 border-lavender bg-lavender-pale px-3 py-2 text-ink-dark" data-id="element-871" data-mp="j3a7c15gkmwe">{referral.sharedInfo}</dd>
        </div>
        <div data-id="element-872" data-mp="9bnrfri6dznb">
          <dt className="font-semibold text-ink-dark" data-id="element-873" data-mp="9c5v39763g59">Participant agreement</dt>
          <dd className="text-ink-dark" data-id="element-874" data-mp="5c546p2vtchb">Confirmed for this purpose and this information only · {formatTimestamp(referral.createdAt)}</dd>
        </div>
      </dl>

      <h4 className="mt-5 text-sm font-semibold text-ink-dark" data-id="element-875" data-mp="3ribuzcxkueqv">Status history</h4>
      <ol className="mt-2 space-y-1.5 text-sm" data-id="element-876" data-mp="3rkej1rf65xsy">
        {referral.updates.map((update, index) => <li key={`${update.at}-${index}`} className="text-ink-muted" data-id="element-877" data-mp="3mlleiohv1h4x" data-mpi={index}>
            <span className="font-semibold text-ink-dark" data-id="element-878" data-mp="1pqf8j89vfkhb">{referralStatusLabels[update.status]}</span> · {formatTimestamp(update.at)} — {update.reason}
          </li>)}
      </ol>

      {canUpdate && <form onSubmit={submit} noValidate className="mt-5 grid grid-cols-1 gap-3 border-t border-line pt-4 sm:grid-cols-[12rem_1fr_auto] sm:items-end" data-id="element-879" data-mp="1t8mgce0zmv18">
          <div data-id="element-880" data-mp="220wqjvbvv0ih">
            <label htmlFor={`${referral.id}-status`} className={labelClass} data-id="element-881" data-mp="2227x22wpqmda">
              New status
            </label>
            <select id={`${referral.id}-status`} value={draft.status} onChange={event => dispatch({
          type: 'setReferralUpdateDraft',
          referralId: referral.id,
          draft: {
            ...draft,
            status: event.target.value as ReferralStatus
          }
        })} className={fieldClass} data-id="element-882" data-mp="26zhsbz6s9s91">
              <option value="" data-id="element-883" data-mp="1oab7o6mfiqkl">Choose…</option>
              {statuses.filter(status => status !== referral.status).map(status => <option key={status} value={status} data-id="element-884" data-mp="2zoyggkndemzg">
                    {referralStatusLabels[status]}
                  </option>)}
            </select>
          </div>
          <div data-id="element-885" data-mp="cilnlleth31x">
            <label htmlFor={`${referral.id}-reason`} className={labelClass} data-id="element-886" data-mp="7heenj0xtxto">
              Reason
            </label>
            <input id={`${referral.id}-reason`} value={draft.reason} onChange={event => dispatch({
          type: 'setReferralUpdateDraft',
          referralId: referral.id,
          draft: {
            ...draft,
            reason: event.target.value
          }
        })} className={fieldClass} data-id="element-887" data-mp="lhp5im28zceu" />
          </div>
          <button type="submit" className={primaryButton} data-id="element-888" data-mp="qf7hklbka7r2">
            Update status
          </button>
          {error && <p className={`${errorClass} sm:col-span-3`} data-id="element-889" data-mp="146dp94ozvbp1">{error}</p>}
        </form>}
    </article>;
}